import { useState } from "react";

interface ColumnEditorProps {
  columns: string[];
  onAddColumn: (name: string) => void;
  onRemoveColumn: (name: string) => void;
  /**
   * R-76a — rename an existing column. The backend migrates every entry's
   * `data` key old→new (positional rename) so renaming a `col_N` produced
   * by a headerless import doesn't orphan its data.
   */
  onRenameColumn: (oldName: string, newName: string) => void;
}

export function ColumnEditor({
  columns,
  onAddColumn,
  onRemoveColumn,
  onRenameColumn,
}: ColumnEditorProps) {
  const [newColumnName, setNewColumnName] = useState("");

  function handleAdd() {
    const trimmed = newColumnName.trim();
    if (!trimmed) return;
    onAddColumn(trimmed);
    setNewColumnName("");
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAdd();
    }
  }

  function commitRename(oldName: string, raw: string, inputEl: HTMLInputElement) {
    const next = raw.trim();
    if (
      !next ||
      next === oldName ||
      columns.some((c) => c !== oldName && c === next)
    ) {
      // No-op / invalid / duplicate — revert the field to the real name.
      inputEl.value = oldName;
      return;
    }
    onRenameColumn(oldName, next);
  }

  return (
    <div className="lt-column-editor">
      <h3 className="lt-section-title">Columns</h3>

      <ul className="lt-column-list">
        {columns.map((col) => (
          <li key={col} className="lt-column-row">
            <input
              type="text"
              className="lt-column-name lt-dialog-input"
              defaultValue={col}
              aria-label={`Rename column ${col}`}
              onBlur={(e) => commitRename(col, e.currentTarget.value, e.currentTarget)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  e.currentTarget.blur();
                } else if (e.key === "Escape") {
                  e.currentTarget.value = col;
                  e.currentTarget.blur();
                }
              }}
            />
            <button
              type="button"
              className="lt-column-remove"
              aria-label={`Remove ${col}`}
              onClick={() => onRemoveColumn(col)}
            >
              &times;
            </button>
          </li>
        ))}
        {columns.length === 0 && (
          <li className="lt-column-empty">No columns defined.</li>
        )}
      </ul>

      <div className="lt-column-add-row">
        <input
          type="text"
          className="lt-dialog-input"
          placeholder="Column name"
          value={newColumnName}
          onChange={(e) => setNewColumnName(e.target.value)}
          onKeyDown={handleKeyDown}
          aria-label="New column name"
        />
        <button type="button" className="rb-btn rb-btn-save lt-column-add-btn" onClick={handleAdd}>
          Add
        </button>
      </div>
    </div>
  );
}
